import { Component, computed, effect, signal, viewChild } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { getCountriesInfo, formatPhoneNumber, isPhoneNumberValid } from '@dss-libs/shared/utility/common-utilities';
import { ICountryInfo, PhoneNumberValue } from '@dss-libs/shared/type/common-types';
import { FormValueClassAbstract } from '../../../abstract/form-value-class-abstract';
import { JunoPhoneNumberCountryPicker } from '../juno-phone-number-country-picker/juno-phone-number-country-picker';
import { JunoError } from '../juno-error/juno-error';

/**
 * A styled, accessible composite phone-number field built on {@link FormValueClassAbstract}
 * and integrated with Angular Signal Forms.
 *
 * The component combines {@link JunoPhoneNumberCountryPicker} (country-code selector)
 * with a `<input type="tel">` for the local number. Both are rendered inside a flex
 * row that is always `dir="ltr"` so the country picker always appears on the left
 * regardless of the page's writing direction.
 *
 * Internal state (`countryCode`, `phoneNumberText`) is kept in sync with the external
 * `value` signal via a reactive `effect`. User interactions write back to `value`
 * directly so Signal Forms sees a single atomic update per change.
 *
 * Formatting (`formatPhoneNumber`) and char-filtering run on blur and on input
 * respectively; validation is intentionally left to the parent schema.
 *
 * ---
 *
 * ### Inputs (own)
 *
 * None. All presentational inputs are inherited.
 *
 * ### Inputs (inherited from {@link FormValueClassAbstract})
 *
 * `value` ({@link PhoneNumberValue}), `label`, `labelClass`, `placeholder`, `name`,
 * `appliedClasses`, `required`, `errors`, `pending`, `touched`, `dirty`, `disabled`.
 *
 * ### Outputs (inherited)
 *
 * `touch` – emitted when either the country picker closes or the phone input is blurred.
 *
 * ---
 *
 * @usageNotes
 *
 * #### With Signal Forms (`[formField]`)
 * ```typescript
 * const model = signal<{ phone: PhoneNumberValue }>({
 *   phone: { countryCode: '', phoneNumber: '' },
 * });
 * const myForm = form(model, (path) => {
 *   required(path.phone, { message: 'Phone is required.' });
 *   validate(path.phone, ({ value }) => {
 *     const { countryCode, phoneNumber } = value();
 *     return isPhoneNumberValid(phoneNumber, countryCode)
 *       ? undefined
 *       : { kind: 'invalidPhone', message: 'Enter a valid phone number.' };
 *   });
 * });
 * ```
 * ```html
 * <juno-libs-ui-phone-number
 *   [formField]="myForm.phone"
 *   label="PHONE_NUMBER"
 *   name="phone"
 * />
 * ```
 *
 * #### Standalone two-way binding (`[(value)]`)
 * ```html
 * <juno-libs-ui-phone-number [(value)]="phoneSignal" label="PHONE_NUMBER" />
 * ```
 *
 * @see {@link FormValueClassAbstract} for the full list of inherited inputs/outputs.
 * @see {@link JunoPhoneNumberCountryPicker} for the embedded country-code picker.
 * @see {@link PhoneNumberValue} for the value shape.
 */
@Component({
  selector: 'juno-libs-ui-phone-number',
  imports: [TranslocoDirective, JunoPhoneNumberCountryPicker, JunoError],
  templateUrl: './juno-phone-number.html',
  styleUrl: './juno-phone-number.css',
})
export class JunoPhoneNumber extends FormValueClassAbstract<PhoneNumberValue, string> {
  readonly allCountries = signal(getCountriesInfo()).asReadonly();
  readonly pickerRef = viewChild.required(JunoPhoneNumberCountryPicker);

  protected readonly countryCode = signal('');
  protected readonly phoneNumberText = signal('');

  protected readonly selectedCountry = computed<ICountryInfo | null>(
    () => this.allCountries().find((country) => country.code === this.countryCode()) ?? null,
    { equal: (a, b) => a?.code === b?.code }
  );

  readonly syncFromValue = effect(() => {
    const value = this.value();
    const countryCode = value?.countryCode || 'US';
    this.countryCode.set(countryCode);
    this.phoneNumberText.set(value?.phoneNumber ?? '');
    if (value !== undefined && !value.countryCode) {
      this.value.set({ countryCode: 'US', phoneNumber: value.phoneNumber });
    }
  });

  protected updateCountry(code: string | undefined): void {
    const resolved = code ?? '';
    this.countryCode.set(resolved);
    this.value.set({ countryCode: resolved, phoneNumber: this.phoneNumberText() });
  }

  protected onPhoneInput(event: Event): void {
    const inputEl = event.target as HTMLInputElement;
    const raw = inputEl.value.replace(/[^0-9\-()+]/g, '');
    inputEl.value = raw;
    this.phoneNumberText.set(raw);
    this.value.set({ countryCode: this.countryCode(), phoneNumber: raw });
  }

  protected onPhoneBlur(): void {
    const countryCode = this.countryCode();
    const phoneNumber = this.phoneNumberText();
    if (countryCode && phoneNumber && isPhoneNumberValid(phoneNumber, countryCode)) {
      const formatted = formatPhoneNumber(phoneNumber, countryCode);
      if (formatted !== phoneNumber) {
        this.phoneNumberText.set(formatted);
        this.value.set({ countryCode: countryCode, phoneNumber: formatted });
      }
    }
    this.touch.emit();
  }

  focus(options?: FocusOptions): void {
    this.pickerRef().focus(options);
  }
}
